import { useEffect, useState } from 'react'
import { ShieldAlert } from 'lucide-react'
import { coreConfig } from '@hk/core'
import type { AppLocale } from '@hk/ui'

/* THE PREVIEW BANNER.

   The API can be run with a preview identity provider that performs NO authentication
   at all — anyone who can reach the server signs in as anyone by typing an employee
   number. It exists so real supervisors can review the screens on the client's own
   server while TQ-01 is unanswered.

   This banner is one of the three things that keep that honest, and it is why it cannot
   be dismissed and does not scroll away. A preview nobody can tell is a preview is the
   whole failure mode: somebody takes a screenshot of a room board, it reaches a
   WhatsApp group six weeks later, and by then it is evidence of something.

   It asks the SERVER what it is running rather than reading a build-time flag. A build
   flag says what someone intended when they built; /health says what is actually bound
   in the process answering right now, which is the only version that cannot be wrong. */

const PREVIEW_MARKER = 'preview-NO-REAL-AUTHENTICATION'

const TEXT = {
  en: {
    title: 'Preview server — there is no sign-in security here.',
    body: 'Anyone who can reach this address can act as any member of staff. Do not enter '
      + 'real guest or staff information, and do not treat anything on screen as a record.',
  },
  ar: {
    title: 'خادم معاينة — لا يوجد أي تأمين لتسجيل الدخول هنا.',
    body: 'يمكن لأي شخص يصل إلى هذا العنوان أن يتصرف باسم أي موظف. لا تُدخل بيانات حقيقية '
      + 'للنزلاء أو الموظفين، ولا تعتبر ما يظهر على الشاشة سجلًا رسميًا.',
  },
} as const

export function PreviewBanner({ locale }: { locale: AppLocale }) {
  const [isPreview, setIsPreview] = useState(false)

  useEffect(() => {
    const controller = new AbortController()

    // A failure here means "not a preview" — an unreachable /health is its own visible
    // problem (the board will not load either) and must not put a false warning on a
    // production screen.
    fetch(`${coreConfig().apiBaseUrl.replace(/\/api\/v1$/, '')}/health`,
      { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((body: { identityProvider?: string } | null) => {
        if (body?.identityProvider === PREVIEW_MARKER) setIsPreview(true)
      })
      .catch(() => { /* see above */ })

    return () => controller.abort()
  }, [])

  if (!isPreview) return null

  const t = TEXT[locale]

  return (
    <div
      role="alert"
      className="flex items-start gap-2.5 border-b-2 border-[#b91c1c] bg-[#fef2f2] px-4 py-2
                 text-[12px] text-[#7f1d1d] dark:bg-[#2b0f0f] dark:text-[#fecaca]"
    >
      <ShieldAlert className="mt-px size-4 shrink-0" />
      <p className="leading-snug">
        <strong className="font-semibold">{t.title}</strong>{' '}
        <span className="opacity-90">{t.body}</span>
      </p>
    </div>
  )
}
